import { type FeatureKeyLike } from '@adtrackify/at-tracking-event-types';
import { ArrowRight, CheckCircle2, Lock, Zap } from 'lucide-react';
import { ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatPlanDisplayName } from '@/features/billing/helpers/subscription-display-helpers';
import { Button } from '@/components/ui/button';
import { DIALOG_DISMISSAL, DIALOG_SIZE } from '@/components/ui/types/dialog-variants-enums';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useFeatureEntitlement } from '@/features/billing/hooks/use-feature-entitlement';
import { useFeatureUpgradePath } from '@/features/billing/hooks/use-feature-upgrade-path';
import { cn } from '@/lib/utils';
import { buildOverlayStyles } from './helpers/build-overlay-styles';

// ============================================================================
// Types
// ============================================================================

interface FeatureGateOverlayProps {
  featureKey: FeatureKeyLike;
  children: ReactNode;
  featureName?: string;
  title?: string;
  description?: string;
  className?: string;
  preview?: boolean;
  visiblePercent?: number;
  benefits?: string[];
}

interface FeatureGateProps {
  featureKey: FeatureKeyLike;
  children: ReactNode;
  fallback?: ReactNode;
}

interface FeatureGateModalProps {
  featureKey: FeatureKeyLike;
  featureName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  description?: string;
  benefits?: string[];
}

// ============================================================================
// Upgrade prompt (inline card)
// ============================================================================

function UpgradePrompt({
  featureKey,
  featureName,
  title,
  description,
  benefits,
  compact = false,
}: {
  featureKey: FeatureKeyLike;
  featureName: string;
  title: string;
  description: string;
  benefits?: string[];
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const { currentPlan, currentPlanGroupName } = useFeatureUpgradePath(featureKey);
  const currentPlanDisplayName = currentPlan?.displayName
    ? formatPlanDisplayName(currentPlan.displayName, currentPlanGroupName)
    : undefined;

  const handleUpgrade = useCallback(() => {
    void navigate('/account/stripe-plan');
  }, [navigate]);

  const handleChangePlan = useCallback(() => {
    void navigate('/account/stripe-plan');
  }, [navigate]);

  const defaultBenefits = [
    `Full access to ${featureName}`,
    'All analytics and reporting',
    'Priority support',
  ];
  const displayBenefits = benefits ?? defaultBenefits;

  return (
    <div
      className={cn(
        'mx-auto w-full rounded-2xl border border-border/60 bg-background text-center shadow-card-elevated',
        compact ? 'max-w-md p-6' : 'max-w-lg p-8',
      )}
    >
      {/* Icon */}
      <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5">
        <Zap className="h-7 w-7 text-primary" />
      </div>

      {/* Headline — action-oriented */}
      <h3 className={cn('mb-2 font-bold text-foreground', compact ? 'text-lg' : 'text-xl')}>
        {title}
      </h3>
      <p className="mb-5 text-sm leading-relaxed text-muted-foreground max-w-sm mx-auto">
        {description}
      </p>

      {/* Benefits list */}
      {!compact && displayBenefits.length > 0 && (
        <div className="mb-6 space-y-2.5 text-left max-w-xs mx-auto">
          {displayBenefits.map(benefit => (
            <div key={benefit} className="flex items-start gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-status-success mt-0.5 shrink-0" />
              <span className="type-body text-content-secondary">{benefit}</span>
            </div>
          ))}
        </div>
      )}

      {/* Primary CTA */}
      <Button onClick={handleUpgrade} className="w-full font-semibold" size="lg">
        Upgrade Plan
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>

      {/* Secondary link */}
      <button
        type="button"
        onClick={handleChangePlan}
        className="mt-3 inline-block type-settings-row-meta text-muted-foreground hover:text-foreground transition-colors underline-offset-2 hover:underline"
      >
        Change Plan
      </button>

      {/* Current plan context */}
      {currentPlanDisplayName && (
        <p className="mt-2 type-micro text-muted-foreground/70">
          You&apos;re currently on {currentPlanDisplayName}
          {currentPlan?.displayPrice ? ` (${currentPlan.displayPrice})` : ''}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// FeatureGateOverlay — inline content gate
// ============================================================================

export function FeatureGateOverlay({
  featureKey,
  children,
  featureName = 'this feature',
  title,
  description,
  className,
  preview = false,
  visiblePercent = 40,
  benefits,
}: FeatureGateOverlayProps) {
  const { isEnabled, isLoading } = useFeatureEntitlement(featureKey);

  if (isLoading) {
    return <div className={cn('h-[calc(100vh-180px)]', className)} />;
  }

  if (isEnabled) {
    return <>{children}</>;
  }

  const resolvedTitle = title ?? `Unlock ${featureName}`;
  const resolvedDescription =
    description ??
    `See deeper insights and make better decisions with ${featureName}. Upgrade your plan to get started.`;

  if (preview) {
    const { scrimGradient } = buildOverlayStyles(visiblePercent);

    return (
      <div className={cn('relative min-h-[800px]', className)}>
        {/* Real content at natural height, non-interactive */}
        <div className="pointer-events-none select-none" aria-hidden="true">
          {children}
        </div>

        {/* White scrim — fades from transparent to solid white */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: scrimGradient }}
        />

        {/* Upgrade prompt — fixed to center of content area, pointer-events only on the card itself */}
        <div className="fixed left-1/2 top-[calc(50vh+32px)] z-30 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          <div className="pointer-events-auto">
          <UpgradePrompt
            featureKey={featureKey}
            featureName={featureName}
            title={resolvedTitle}
            description={resolvedDescription}
            benefits={benefits}
            compact
          />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-[calc(100vh-180px)] items-center justify-center bg-primary/5', className)}>
      <UpgradePrompt
        featureKey={featureKey}
        featureName={featureName}
        title={resolvedTitle}
        description={resolvedDescription}
        benefits={benefits}
      />
    </div>
  );
}

// ============================================================================
// FeatureGate — conditional render (hide/show)
// ============================================================================

export function FeatureGate({ featureKey, children, fallback = null }: FeatureGateProps) {
  const { isEnabled, isLoading } = useFeatureEntitlement(featureKey);

  if (isLoading || !isEnabled) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}

// ============================================================================
// FeatureGateModal — dialog-based gate for click actions
// ============================================================================

export function FeatureGateModal({
  featureKey,
  featureName,
  open,
  onOpenChange,
  description,
  benefits,
}: FeatureGateModalProps) {
  const navigate = useNavigate();
  const { currentPlan, currentPlanGroupName } = useFeatureUpgradePath(featureKey);
  const currentPlanDisplayName = currentPlan?.displayName
    ? formatPlanDisplayName(currentPlan.displayName, currentPlanGroupName)
    : undefined;

  const defaultBenefits = [
    `Full access to ${featureName}`,
    'Advanced analytics and reporting',
  ];
  const displayBenefits = benefits ?? defaultBenefits;

  const handleUpgrade = useCallback(() => {
    onOpenChange(false);
    void navigate('/account/stripe-plan');
  }, [navigate, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="overflow-hidden p-0 sm:max-w-[420px]"
        dismissal={DIALOG_DISMISSAL.STANDARD}
        size={DIALOG_SIZE.SM}
      >
        {/* Gradient header band */}
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-8 pb-4">
          <DialogHeader className="space-y-3">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15">
              <Lock className="h-5 w-5 text-primary" />
            </div>
            <DialogTitle className="text-center text-lg">
              Unlock {featureName}
            </DialogTitle>
            <DialogDescription className="text-center text-sm leading-relaxed">
              {description ??
                `${featureName} is available on a higher plan. Upgrade to unlock this feature.`}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-6 space-y-4">
          {/* Benefits */}
          {displayBenefits.length > 0 && (
            <div className="space-y-2 pt-1">
              {displayBenefits.map(benefit => (
                <div key={benefit} className="flex items-center gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-status-success shrink-0" />
                  <span className="type-body text-content-secondary">{benefit}</span>
                </div>
              ))}
            </div>
          )}

          {/* Current plan context */}
          {currentPlanDisplayName && (
            <p className="type-micro text-muted-foreground text-center">
              You&apos;re currently on {currentPlanDisplayName}
              {currentPlan?.displayPrice ? ` (${currentPlan.displayPrice})` : ''}
            </p>
          )}

          {/* CTAs */}
          <DialogFooter className="flex-col gap-2 sm:flex-col pt-1">
            <Button onClick={handleUpgrade} className="w-full font-semibold" size="lg">
              Upgrade Plan
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <button
              type="button"
              className="w-full text-center type-settings-row-meta text-muted-foreground hover:text-foreground transition-colors py-1"
              onClick={() => onOpenChange(false)}
            >
              Not right now
            </button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
